import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import useOperationProgress from './useOperationProgress';

/**
 * Hook para gerenciar status dos exaustores com polling automático
 * Encapsula lógica complexa: AbortController, RequestId, Timeout, Polling
 * 
 * @param {number|string} selectedBloco - ID do bloco selecionado
 * @param {number} activeCondominio - ID do condomínio ativo
 * @returns {Object} { status, remainingTimes, statusError, isSpinningStatus, finaisSemLeitura, getStatus, resetStatus }
 */
// Maior que STATUS_TOTAL_BUDGET_MS (22s) + maior tentativa isolada (6s) do backend
const STATUS_REQUEST_TIMEOUT_MS = 30000;
// Consulta so ao banco (temporizadores)
const TEMPORIZADORES_REQUEST_TIMEOUT_MS = 15000;

const useExaustorStatus = (selectedBloco, activeCondominio) => {
  const apiUrl = process.env.REACT_APP_API_URL;

  // Estados
  const [status, setStatus] = useState({});
  
  const [remainingTimes, setRemainingTimes] = useState({});
  
  const [statusError, setStatusError] = useState(null);
  const [isSpinningStatus, setIsSpinningStatus] = useState(false);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  const [isStatusParcial, setIsStatusParcial] = useState(false);
  const [finaisSemLeitura, setFinaisSemLeitura] = useState([]);

  // Refs para AbortController, RequestId e valores atuais (evita deps infinitas)
  const statusAbortControllerRef = useRef(null);
  const statusRequestIdRef = useRef(0);
  const currentBlocoRef = useRef(selectedBloco);
  const currentCondominioRef = useRef(activeCondominio);

  // Progresso real de retry por modulo (leve, so para um indicador agregado
  // de "revalidando" — nao mapeia por card individual, ver plano da feature).
  const { progress: moduleProgress, start: startModuleProgress, reset: resetModuleProgress } = useOperationProgress();

  // Sincronizar refs com estado atual
  useEffect(() => {
    currentBlocoRef.current = selectedBloco;
  }, [selectedBloco]);

  useEffect(() => {
    currentCondominioRef.current = activeCondominio;
  }, [activeCondominio]);

  // Função para buscar status dos exaustores
  // NÃO usar activeCondominio em dependências - usar ref para evitar recreação infinita
  const getStatus = useCallback(async (blocoId) => {
    // Gerar ID único para esta requisição (para ignorar responses obsoletas)
    const requestId = ++statusRequestIdRef.current;
    
    // Cancelar request anterior de getStatus
    statusAbortControllerRef.current?.abort();
    statusAbortControllerRef.current = new AbortController();
    
    setIsSpinningStatus(true);
    setIsLoadingStatus(true);

    const operationId = startModuleProgress(apiUrl, currentCondominioRef.current);

    try {
      // Timeout do proprio axios: cancela a requisicao de verdade (fecha a
      // conexao), o que permite ao backend perceber e parar de consultar
      // modulos. Com 30s cobre o orcamento de 22s do backend + a maior
      // tentativa isolada (6s, API). Usar ref ao inves de estado.
      const statusPromise = axios.get(`${apiUrl}/api/exaustores/status`, {
        params: { blocoId, operationId },
        headers: {
          'x-condominio-id': currentCondominioRef.current
        },
        signal: statusAbortControllerRef.current.signal,
        timeout: STATUS_REQUEST_TIMEOUT_MS
      });

      const response = await statusPromise;
      
      // ✅ Validar se este resultado ainda é relevante (ignorar responses obsoletas)
      if (requestId !== statusRequestIdRef.current) {
        return;
      }

      if (response.data.dados?.status) {
        const exaustorStatus = response.data.dados.status;

        // Construir dynamicamente baseado na resposta da API. Modulo `ocupado`
        // (comando em andamento) ou `nao_consultado` (orcamento de tempo do
        // backend esgotou) mantem o ultimo status conhecido, em vez de piscar
        // "Erro ao obter status".
        setStatus((prev) => {
          const newStatus = {};
          exaustorStatus.forEach((item) => {
            const chave = `final${item.final}`;
            const semLeitura = item.ocupado || item.nao_consultado;
            newStatus[chave] = semLeitura && prev?.[chave] !== undefined ? prev[chave] : item.status;
          });
          return newStatus;
        });
        // Finais exibindo a ultima leitura conhecida (sem leitura nesta
        // consulta) — so para o card marcar "Ultima leitura". Calculado fora
        // do updater acima, que deve ficar puro.
        setFinaisSemLeitura(
          exaustorStatus
            .filter((item) => item.ocupado || item.nao_consultado)
            .map((item) => `final${item.final}`)
        );
        setIsStatusParcial(Boolean(response.data.dados.parcial));
        setStatusError(null);
        setIsLoadingStatus(false);
      }

      // Fetch remaining times (consulta so ao banco)
      const remainingTimePromise = axios.get(`${apiUrl}/api/exaustores/temporizadores`, {
        params: { blocoId },
        headers: {
          'x-condominio-id': currentCondominioRef.current
        },
        signal: statusAbortControllerRef.current.signal,
        timeout: TEMPORIZADORES_REQUEST_TIMEOUT_MS
      });

      const remainingTimeResponse = await remainingTimePromise;
      
      // ✅ Validar relevância novamente
      if (requestId !== statusRequestIdRef.current) {
        return;
      }
      
      setRemainingTimes(remainingTimeResponse.data.dados?.remainingTimes || {});
    } catch (err) {
      // ✅ Ignorar se foi cancelado (AbortError ou CanceledError do Axios)
      if (err.name === 'AbortError' || err.name === 'CanceledError' || err.code === 'ABORT_ERR' || err.code === 'ERR_CANCELED') {
        return;
      }
      
      // Ignorar se foi timeout ou já foi substituído por outro request
      if (requestId !== statusRequestIdRef.current) {
        return;
      }
      
      if (!err.isRateLimitError) {
        // Não setError aqui - deixar componente pai decidir como tratar
        setStatusError(blocoId);
      }
      setIsLoadingStatus(false);
    } finally {
      // So a requisicao mais recente mexe no spinner e no progresso: uma
      // antiga (cancelada/substituida) nao pode apagar os da nova.
      if (requestId === statusRequestIdRef.current) {
        setIsSpinningStatus(false);
        // reset (e nao so stop): zera tambem o ultimo snapshot, senao o aviso
        // "Revalidando..." ficaria preso ate a proxima consulta
        resetModuleProgress();
      }
    }
  }, [apiUrl, startModuleProgress, resetModuleProgress]); // NÃO INCLUIR activeCondominio para evitar recreação infinita - usar ref

  // Indicador agregado (nao por card): true se algum modulo estiver em uma
  // tentativa de retry (2a ou 3a) no momento — usado so para dar visibilidade
  // de que o backend esta reinsistindo na consulta, sem granularidade por
  // exaustor especifico.
  const isRetryingStatus = useMemo(() => {
    if (!moduleProgress || !moduleProgress.modulos) return false;
    return Object.values(moduleProgress.modulos).some(
      (modulo) => modulo.estado === 'tentando' && modulo.tentativaAtual > 1
    );
  }, [moduleProgress]);

  // Countdown timer: decrementa remainingTimes a cada segundo
  useEffect(() => {
    const interval = setInterval(() => {
      setRemainingTimes((prevTimes) => {
        const newTimes = { ...prevTimes };
        Object.keys(newTimes).forEach((exaustor) => {
          if (newTimes[exaustor] > 0) {
            newTimes[exaustor] -= 1;
          }
        });
        return newTimes;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Polling automático: atualiza status a cada 5 minutos
  // NÃO incluir getStatus nas dependências para evitar ciclo infinito
  useEffect(() => {
    if (!selectedBloco) return;

    // Disparar getStatus imediatamente quando bloco muda
    getStatus(selectedBloco);

    // Atualizar a cada 5 minutos usando refs para ter sempre valores atuais
    const interval = setInterval(() => {
      if (currentBlocoRef.current) {
        getStatus(currentBlocoRef.current);
      }
    }, 300000); // 5 minutos

    // Cleanup: parar interval e cancelar requisição pendente ao desmontar ou trocar bloco
    return () => {
      clearInterval(interval);
      statusAbortControllerRef.current?.abort();
    };
  }, [selectedBloco, getStatus]);

  // Função para resetar status manualmente
  const resetStatus = useCallback(() => {
    setStatus({});
    setRemainingTimes({});
    setStatusError(null);
    setIsLoadingStatus(false);
    setIsStatusParcial(false);
    setFinaisSemLeitura([]);
  }, []);

  return {
    status,
    remainingTimes,
    statusError,
    isSpinningStatus,
    isLoadingStatus,
    isRetryingStatus,
    isStatusParcial,
    finaisSemLeitura,
    getStatus,
    resetStatus,
  };
};

export default useExaustorStatus;
